import React from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GridTable from '../../components/GridTable';
import Layout from '../../components/Layout';
import { MODULES } from '../../utils/constants';
import {
    fetchExternalCatalogRows,
    syncExternalCatalog,
    updateExternalCatalogMetadata,
} from '../../services/externalCatalog';
import { showError, showSuccess } from '../../services/toaster';
import { fetchCamps } from '../../services/camps';
import { omitEmptyOptionalFields, uploadFile } from '../../utils/helper';
import { getVendorIntegration, isVendorUser } from '../../utils/helper';
import { useAppContext } from '../../services/context/AppContext';
import {
    ExternalCatalogIntegration,
    ExternalCatalogItemType,
    ExternalCatalogRow
} from '../../types/externalCatalog';
import { Camp } from '../../types/camps';
import { getColumns } from './constants';
import EditExternalCatalogDialog from './EditExternalCatalogDialog';

const ROWS_PER_PAGE = 20;

const resolveUploadedFileUrl = (uploadResponse: any): string | undefined => {
    const firstFile = uploadResponse?.data?.data?.files?.[0]
        || uploadResponse?.data?.data?.[0]
        || undefined;

    if (!firstFile) {
        return undefined;
    }

    if (typeof firstFile === 'string') {
        return firstFile;
    }

    if (typeof firstFile === 'object' && typeof firstFile.url === 'string') {
        return firstFile.url;
    }

    return undefined;
};

const ExternalCatalog = () => {
    const { userData } = useAppContext();
    const isVendor = isVendorUser(userData);
    const vendorIntegration = getVendorIntegration(userData) as ExternalCatalogIntegration;
    const [integration, setIntegration] = React.useState<ExternalCatalogIntegration>('labstack');
    const [itemType, setItemType] = React.useState<ExternalCatalogItemType>('package');
    const [rows, setRows] = React.useState<ExternalCatalogRow[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<ExternalCatalogRow[]>([]);
    const [search, setSearch] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const [dialogRow, setDialogRow] = React.useState<ExternalCatalogRow | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [camps, setCamps] = React.useState<Camp[]>([]);

    const loadRows = React.useCallback(async (
        nextItemType: ExternalCatalogItemType,
        nextIntegration: ExternalCatalogIntegration
    ) => {
        setLoading(true);
        try {
            const data = await fetchExternalCatalogRows(
                nextIntegration,
                nextItemType,
                undefined,
                true
            );
            setRows(data);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadRows(itemType, integration);
    }, [itemType, integration, loadRows]);

    React.useEffect(() => {
        if (isVendor && vendorIntegration && vendorIntegration !== integration) {
            setIntegration(vendorIntegration);
        }
    }, [integration, isVendor, vendorIntegration]);

    React.useEffect(() => {
        let mounted = true;
        fetchCamps()
            .then((data) => {
                if (mounted) setCamps(data);
            })
            .catch(() => {
                if (mounted) setCamps([]);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const results = await syncExternalCatalog(integration, itemType);
            const summary = results[0];
            if (!summary) {
                throw new Error('Catalog sync did not return a result');
            }
            await loadRows(itemType, integration);
            showSuccess(
                `Sync complete: ${summary.created} new, ${summary.updated} updated, ${summary.disabled} disabled`
            );
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Catalog sync failed');
        } finally {
            setSyncing(false);
        }
    };

    React.useEffect(() => {
        const term = search.trim().toLowerCase();
        const next = term
            ? rows.filter((row) =>
                row.name.toLowerCase().includes(term) ||
                row.providerName.toLowerCase().includes(term) ||
                row.externalItemId.toLowerCase().includes(term)
            )
            : rows;
        setFilteredRows(next);
        setCurrentPage(1);
    }, [rows, search]);

    const pagedRows = React.useMemo(() => {
        const start = (currentPage - 1) * ROWS_PER_PAGE;
        return filteredRows.slice(start, start + ROWS_PER_PAGE);
    }, [filteredRows, currentPage]);

    const handleSave = async (value: {
        termsAndConditions?: string;
        name?: string;
        pricingMode?: 'absolute' | 'markupAmount' | 'markupPercentage';
        customPrice?: number | null;
        customMrp?: number | null;
        markupPercentage?: number | null;
        markupAmount?: number | null;
        imageFile?: File | null;
        coverImageFile?: File | null;
        existingImage?: string;
        existingCoverImage?: string;
        pinned?: boolean;
        enabled?: boolean;
        gender?: 'male' | 'female' | '';
        applicableAge?: number | null;
        campId?: string;
        campIds?: string[];
        reportEscalationHours?: number | null;
    }) => {
        if (!dialogRow?._id) {
            return;
        }

        setSaving(true);
        try {
            let imageUrl = value.existingImage;
            let coverImageUrl = value.existingCoverImage;

            if (value.imageFile) {
                const uploadResponse = await uploadFile(
                    { module: 'external-bookings', recordId: dialogRow._id },
                    [value.imageFile]
                );
                const uploadedPath = resolveUploadedFileUrl(uploadResponse);
                if (uploadedPath) {
                    imageUrl = uploadedPath;
                }
            }

            if (value.coverImageFile) {
                const uploadResponse = await uploadFile(
                    { module: 'external-bookings', recordId: dialogRow._id },
                    [value.coverImageFile]
                );
                const uploadedPath = resolveUploadedFileUrl(uploadResponse);
                if (uploadedPath) {
                    coverImageUrl = uploadedPath;
                }
            }

            const updatePayload = omitEmptyOptionalFields({
                name: value.name,
                customPrice: value.customPrice ?? undefined,
                customMrp: value.customMrp ?? undefined,
                markupPercentage: value.markupPercentage ?? undefined,
                markupAmount: value.markupAmount ?? undefined,
                image: imageUrl,
                coverImage: coverImageUrl,
                pinned: value.pinned,
                enabled: value.enabled,
                ...(value.enabled ? { status: 2 as const } : {}),
                gender: dialogRow.itemType === 'package' ? (value.gender || null) : undefined,
                applicableAge: dialogRow.itemType === 'package' ? value.applicableAge : undefined,
                campId: dialogRow.itemType === 'package' && value.campId ? value.campId : undefined,
                campIds: dialogRow.itemType === 'package' ? (value.campIds || []) : undefined,
            });
            if (dialogRow.itemType === 'package' && !value.gender) {
                (updatePayload as any).gender = null;
            }
            if (dialogRow.itemType === 'package' && value.applicableAge === null) {
                (updatePayload as any).applicableAge = null;
            }
            if (value.customPrice === null) {
                (updatePayload as any).customPrice = null;
            }
            if (value.customMrp === null) {
                (updatePayload as any).customMrp = null;
            }
            if (value.markupPercentage === null) {
                (updatePayload as any).markupPercentage = null;
            }
            if (value.markupAmount === null) {
                (updatePayload as any).markupAmount = null;
            }

            if (dialogRow.itemType === 'package') {
                (updatePayload as any).termsAndConditions = value.termsAndConditions || '';
                // null clears it back to the 48-hour default.
                if (value.reportEscalationHours !== undefined) {
                    (updatePayload as any).reportEscalationHours = value.reportEscalationHours;
                }
            }
            await updateExternalCatalogMetadata(dialogRow._id, updatePayload);
            await loadRows(itemType, integration);
            setDialogRow(null);
            showSuccess('Catalog item updated');
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to update catalog item');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout appBarTitle="External Catalog">
            <Box sx={{ display: 'grid', gap: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6">External Packages and Tests</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage master availability for synced provider catalog items.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {!isVendor && (
                            <FormControl size="small" sx={{ minWidth: 160, backgroundColor: '#fff' }}>
                                <InputLabel>Provider</InputLabel>
                                <Select
                                    label="Provider"
                                    value={integration}
                                    onChange={(e) => setIntegration(e.target.value as ExternalCatalogIntegration)}
                                >
                                    <MenuItem value="labstack">Labstack</MenuItem>
                                    <MenuItem value="healthians">Healthians</MenuItem>
                                    <MenuItem value="betacura">Betacura</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        <Button
                            variant="outlined"
                            onClick={handleSync}
                            disabled={loading || syncing}
                            startIcon={<RefreshIcon />}
                        >
                            {syncing ? 'Syncing...' : 'Sync'}
                        </Button>
                    </Stack>
                </Stack>

                <Tabs
                    value={itemType}
                    onChange={(_e, value) => setItemType(value)}
                    sx={{ backgroundColor: '#fff', borderRadius: 2, px: 1 }}
                >
                    <Tab label="Packages" value="package" />
                    <Tab label="Tests" value="test" />
                </Tabs>

                <Box
                    component="input"
                    value={search}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    placeholder={`Search ${itemType}s by name or external ID`}
                    sx={{
                        border: '1px solid #d0d7de',
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                        fontSize: 14,
                        backgroundColor: '#fff',
                    }}
                />

                <GridTable
                    data={pagedRows}
                    columns={getColumns(
                        currentPage,
                        ROWS_PER_PAGE,
                        isVendor ? undefined : (row) => setDialogRow(row)
                    )}
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE))}
                    total={filteredRows.length}
                    loading={loading}
                    onPageChange={(_event, page) => setCurrentPage(page + 1)}
                    module={MODULES.NOTIFICATION}
                    rows={ROWS_PER_PAGE}
                    hideAction
                />
            </Box>

            <EditExternalCatalogDialog
                open={Boolean(dialogRow)}
                row={dialogRow}
                camps={camps}
                saving={saving}
                corporateScope={false}
                showTerms
                onClose={() => setDialogRow(null)}
                onSubmit={handleSave}
            />
        </Layout>
    );
};

export default ExternalCatalog;
